import { 
    ArrowLeft, 
    AlertCircle, 
    CheckCircle2, 
    Settings2, 
    Search, 
    Blocks, 
    Plus,
    PlusCircle, 
    Loader2, 
    Save, 
    Trash2, 
    Copy, 
    Check, 
    Hash,
    Tag,
    Globe,
    Type,
    Database
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SwitchWithLabel } from '@/components/ui/switch-with-label';
import { Combobox } from '@/components/ui/combobox';
import { SiteHeader } from '@/components/site-header';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import { createKnowledgeBase, deleteKnowledgeBase, getKnowledgeBaseById, updateKnowledgeBase } from '@/api/knowledge-base/knowledge-base.api';
import type { KnowledgeBase, CreateKnowledgeBaseRequest, UpdateKnowledgeBaseRequest, SearchType } from '@/api/knowledge-base/knowledge-base.types';
import { getAllFiles } from '@/api/files/files.api';
import type { FileDetails } from '@/api/files/files.types';
import { Skeleton } from "@/components/ui/skeleton";
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';


const SEARCH_TYPES: { value: SearchType; label: string }[] = [
    { value: 'full_text', label: 'Full Text' },
    { value: 'semantic', label: 'Semantic' },
    { value: 'hybrid', label: 'Hybrid' },
    { value: 'bm25', label: 'BM25' },
];

const formatDelimiter = (delimiter: string): string => {
    const specialChars: Record<string, string> = {
        '\n': '\\n',
        '\t': '\\t',
        '\r': '\\r',
        ' ': '␣'  // visible space character
    };
    return delimiter.replace(/[\n\t\r ]/g, char => specialChars[char] || char);
};

const parseDelimiter = (input: string): string => {
    const specialChars: Record<string, string> = {
        '\\n': '\n',
        '\\t': '\t',
        '\\r': '\r',
        '␣': ' '
    };
    return input.replace(/\\n|\\t|\\r|␣/g, char => specialChars[char] || char);
};

const itemVariants = {
    hidden: {
        opacity: 0,
        y: 5
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.2
        }
    }
};

const KnowledgeBaseForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { error, success } = useEnhancedToast();
    const isEditMode = Boolean(id);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(isEditMode);
    const [files, setFiles] = useState<FileDetails[]>([]);
    const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
    const [isLoadingFiles, setIsLoadingFiles] = useState(false);
    const [newWebsite, setNewWebsite] = useState('');
    const [knowledgeBase, setKnowledgeBase] = useState<KnowledgeBase | null>(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [copied, setCopied] = useState(false);
    const [newDelimiter, setNewDelimiter] = useState('');
    const { user } = useAuth();

    const canEdit = user?.permissions?.knowledge_base === 'EDITOR';

    useEffect(() => {
        fetchFiles();
        if (isEditMode && id) {
            fetchKnowledgeBase();
        } else {
            // Initialize with default values for create mode
            setKnowledgeBase({
                id: '',
                name: '',
                searchType: 'semantic',
                scoreThreshold: 0,
                chunkingConfig: {
                    targetSplitsPerChunk: 20,
                    splitDelimiters: ['.', '!', '?', '\n'],
                    rebalanceChunks: true,
                    removeStopWords: false,
                },
                metadata: {
                    collectionName: '',
                    sources: {
                        fileIds: [],
                        websites: [],
                    },
                },
                createdAt: '',
                updatedAt: '',
                organizationId: '',
            });
        }
    }, [id, isEditMode]);

    const fetchKnowledgeBase = async () => {
        if (!id) return;
        
        try {
            const { data, error: fetchError } = await getKnowledgeBaseById(id);
            if (fetchError) {
                error({
                    icon: AlertCircle,
                    title: "Failed to Load Knowledge Base",
                    description: fetchError,
                    duration: 4000,
                });
                navigate('/dashboard/knowledge-base');
                return;
            }
            if (data) {
                setKnowledgeBase(data);
                setSelectedFileIds(data.metadata?.sources?.fileIds || []);
            }
        } catch {
            error({
                icon: AlertCircle,
                title: "Failed to Load Knowledge Base",
                description: "Could not fetch knowledge base details",
                duration: 4000,
            });
            navigate('/dashboard/knowledge-base');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchFiles = async () => {
        setIsLoadingFiles(true);
        try {
            const { data, error: fetchError } = await getAllFiles();
            if (fetchError) {
                error({
                    icon: AlertCircle,
                    title: "Failed to Load Files",
                    description: fetchError,
                    duration: 4000,
                });
                return;
            }
            if (data) {
                setFiles(data);
            }
        } catch {
            error({
                icon: AlertCircle,
                title: "Failed to Load Files",
                description: "Could not fetch files",
                duration: 4000,
            });
        } finally {
            setIsLoadingFiles(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!knowledgeBase) return;

        setIsSubmitting(true);
        try {
            if (isEditMode) {
                // Update existing knowledge base - flatten nested structure for API
                const updateData: UpdateKnowledgeBaseRequest = {
                    name: knowledgeBase.name,
                    searchType: knowledgeBase.searchType,
                    scoreThreshold: knowledgeBase.scoreThreshold,
                    targetSplitsPerChunk: knowledgeBase.chunkingConfig.targetSplitsPerChunk,
                    splitDelimiters: knowledgeBase.chunkingConfig.splitDelimiters,
                    rebalanceChunks: knowledgeBase.chunkingConfig.rebalanceChunks,
                    removeStopWords: knowledgeBase.chunkingConfig.removeStopWords,
                    fileIds: knowledgeBase.metadata.sources.fileIds,
                    websites: knowledgeBase.metadata.sources.websites,
                };

                const { error: updateError } = await updateKnowledgeBase(id!, updateData);
                if (updateError) {
                    error({
                        icon: AlertCircle,
                        title: "Failed to Update",
                        description: updateError,
                        duration: 4000,
                    });
                    return;
                }

                success({
                    icon: CheckCircle2,
                    title: "Knowledge Base Updated",
                    description: "Successfully updated knowledge base",
                    duration: 3000,
                });
            } else {
                // Create new knowledge base - flatten nested structure for API
                const createData: CreateKnowledgeBaseRequest = {
                    name: knowledgeBase.name,
                    searchType: knowledgeBase.searchType,
                    scoreThreshold: knowledgeBase.scoreThreshold,
                    targetSplitsPerChunk: knowledgeBase.chunkingConfig.targetSplitsPerChunk,
                    splitDelimiters: knowledgeBase.chunkingConfig.splitDelimiters,
                    rebalanceChunks: knowledgeBase.chunkingConfig.rebalanceChunks,
                    removeStopWords: knowledgeBase.chunkingConfig.removeStopWords,
                    fileIds: knowledgeBase.metadata.sources.fileIds,
                    websites: knowledgeBase.metadata.sources.websites,
                };

                const { error: createError } = await createKnowledgeBase(createData);
                if (createError) {
                    error({
                        icon: AlertCircle,
                        title: "Failed to Create",
                        description: createError,
                        duration: 4000,
                    });
                    return;
                }

                success({
                    icon: CheckCircle2,
                    title: "Knowledge Base Created",
                    description: "Successfully created knowledge base",
                    duration: 3000,
                });
                
                // Navigate to list page after creation
                navigate('/dashboard/knowledge-base');
            }
        } catch {
            error({
                icon: AlertCircle,
                title: isEditMode ? "Failed to Update" : "Failed to Create",
                description: "An error occurred. Please try again.",
                duration: 4000,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            const { error: deleteError } = await deleteKnowledgeBase(id!);
            if (deleteError) {
                error({
                    icon: AlertCircle,
                    title: "Failed to Delete",
                    description: deleteError,
                    duration: 4000,
                });
                return;
            }
            success({
                icon: CheckCircle2,
                title: "Knowledge Base Deleted",
                description: "Successfully deleted knowledge base",
                duration: 3000,
            });
            navigate('/dashboard/knowledge-base');
        } catch {
            error({
                icon: AlertCircle,
                title: "Failed to Delete",
                description: "An error occurred while deleting",
                duration: 4000,
            });
        } finally {
            setIsDeleting(false);
            setShowDeleteDialog(false);
        }
    };

    const handleFileSelect = (fileIds: string[]) => {
        setSelectedFileIds(fileIds);
        setKnowledgeBase(prev => {
            if (!prev) return null;
            return {
                ...prev,
                metadata: {
                    ...prev.metadata,
                    sources: {
                        ...prev.metadata.sources,
                        fileIds
                    }
                }
            };
        });
    };

    const handleAddWebsite = () => {
        if (!newWebsite || !knowledgeBase) return;

        try {
            new URL(newWebsite); // Basic URL validation
            
            setKnowledgeBase(prev => {
                if (!prev) return null;
                return {
                    ...prev,
                    metadata: {
                        ...prev.metadata,
                        sources: {
                            ...prev.metadata.sources,
                            websites: [...(prev.metadata.sources.websites || []), newWebsite]
                        }
                    }
                };
            });
            setNewWebsite('');
        } catch {
            error({
                icon: AlertCircle,
                title: "Invalid URL",
                description: "Please enter a valid URL (e.g., https://example.com)",
                duration: 4000,
            });
        }
    };

    const handleRemoveWebsite = (websiteToRemove: string) => {
        if (!knowledgeBase) return;

        setKnowledgeBase(prev => {
            if (!prev) return null;
            return {
                ...prev,
                metadata: {
                    ...prev.metadata,
                    sources: {
                        ...prev.metadata.sources,
                        websites: (prev.metadata.sources.websites || []).filter(w => w !== websiteToRemove)
                    }
                }
            };
        });
    };

    const copyId = async () => {
        if (!knowledgeBase?.id) return;
        
        try {
            await navigator.clipboard.writeText(knowledgeBase.id);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            error({
                icon: AlertCircle,
                title: "Failed to Copy",
                description: "Could not copy ID to clipboard",
                duration: 4000,
            });
        }
    };

    const handleAddDelimiter = () => {
        if (!newDelimiter || !knowledgeBase) return;
        
        const parsedDelimiter = parseDelimiter(newDelimiter);
        
        setKnowledgeBase(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                chunkingConfig: {
                    ...prev.chunkingConfig,
                    splitDelimiters: [...(prev.chunkingConfig.splitDelimiters || []), parsedDelimiter]
                }
            };
        });
        setNewDelimiter('');
    };

    const handleRemoveDelimiter = (delimiterToRemove: string) => {
        if (!knowledgeBase) return;

        setKnowledgeBase(prev => {
            if (!prev) return null;
            return {
                ...prev,
                chunkingConfig: {
                    ...prev.chunkingConfig,
                    splitDelimiters: (prev.chunkingConfig.splitDelimiters || []).filter(d => d !== delimiterToRemove)
                }
            };
        });
    };

    if (isLoading) {
        return (
            <div className="flex flex-col h-full">
                {/* SiteHeader Skeleton */}
                <div className="h-14 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 lg:px-6">
                    <div className="h-full flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <Skeleton className="h-9 w-16" /> {/* Back button */}
                            <div className="space-y-1">
                                <Skeleton className="h-4 w-[180px]" /> {/* Title */}
                                <Skeleton className="h-3 w-[250px]" /> {/* Subtitle */}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Skeleton className="h-9 w-20" /> {/* Delete/Action button */}
                            <Skeleton className="h-9 w-32" /> {/* Save/Create button */}
                        </div>
                    </div>
                </div>

                {/* Form Content Skeleton */}
                <div className="flex-1 overflow-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-4 py-6 md:px-8 md:py-8"
                    >
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Left Column */}
                            <div className="space-y-6">
                                {/* Basic Settings Card Skeleton */}
                                <Card className="shadow-sm">
                                    <CardHeader className="border-b bg-muted/20">
                                        <div className="flex items-center gap-3">
                                            <Skeleton className="h-8 w-8 rounded-lg" /> {/* Icon */}
                                            <div className="flex-1 space-y-1">
                                                <Skeleton className="h-4 w-[150px]" /> {/* Title */}
                                                <Skeleton className="h-3 w-[200px]" /> {/* Description */}
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="p-6 space-y-6">
                                <div className="space-y-2">
                                    <Skeleton className="h-4 w-[100px]" /> {/* Label */}
                                    <Skeleton className="h-9 w-full" /> {/* Input */}
                                </div>
                                <div className="space-y-2">
                                    <Skeleton className="h-4 w-[100px]" />
                                    <Skeleton className="h-9 w-full" />
                                </div>
                                <div className="space-y-2">
                                    <Skeleton className="h-4 w-[100px]" />
                                    <Skeleton className="h-[100px] w-full" /> {/* Multi-select */}
                                </div>
                            </CardContent>
                        </Card>

                                {/* Search Settings Card Skeleton */}
                                <Card className="shadow-sm">
                                    <CardHeader className="border-b bg-muted/20">
                                        <div className="flex items-center gap-3">
                                            <Skeleton className="h-8 w-8 rounded-lg" />
                                            <div className="flex-1 space-y-1">
                                                <Skeleton className="h-4 w-[150px]" />
                                                <Skeleton className="h-3 w-[200px]" />
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="p-6 space-y-6">
                                <div className="space-y-2">
                                    <Skeleton className="h-4 w-[100px]" />
                                    <Skeleton className="h-9 w-full" />
                                </div>
                                <div className="flex items-center gap-2">
                                    <Skeleton className="h-5 w-10" /> {/* Switch */}
                                    <Skeleton className="h-4 w-[150px]" />
                                </div>
                                <div className="space-y-2">
                                    <Skeleton className="h-4 w-[100px]" />
                                    <Skeleton className="h-9 w-full" />
                                </div>
                            </CardContent>
                                </Card>
                            </div>

                            {/* Right Column */}
                            <div className="space-y-6">
                                {/* Chunking Settings Card Skeleton */}
                                <Card className="shadow-sm">
                                    <CardHeader className="border-b bg-muted/20">
                                        <div className="flex items-center gap-3">
                                            <Skeleton className="h-8 w-8 rounded-lg" />
                                            <div className="flex-1 space-y-1">
                                                <Skeleton className="h-4 w-[150px]" />
                                                <Skeleton className="h-3 w-[200px]" />
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="p-6 space-y-6">
                                <div className="space-y-2">
                                    <Skeleton className="h-4 w-[100px]" />
                                    <Skeleton className="h-9 w-full" />
                                </div>
                                <div className="space-y-2">
                                    <Skeleton className="h-4 w-[100px]" />
                                    <Skeleton className="h-9 w-full" />
                                    <Skeleton className="h-[60px] w-full" /> {/* Delimiters list */}
                                </div>
                                <div className="flex items-center gap-2">
                                    <Skeleton className="h-5 w-10" />
                                    <Skeleton className="h-4 w-[150px]" />
                                </div>
                            </CardContent>
                                </Card>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        );
    }

    if (!knowledgeBase) {
        return null;
    }

    return (
        <div className="flex flex-col h-full">
            <SiteHeader
                title={isEditMode ? "Edit Knowledge Base" : "Create Knowledge Base"}
                subtitle={isEditMode ? "Update your knowledge base settings" : "Configure your new knowledge base settings and select files"}
                backButton={
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(-1)}
                        className="gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back
                    </Button>
                }
            >
                {isEditMode && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowDeleteDialog(true)}
                        disabled={isDeleting || !canEdit}
                        className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                        {isDeleting ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Trash2 className="h-4 w-4" />
                        )}
                        Delete
                    </Button>
                )}
                <Button 
                    type="submit" 
                    form="knowledge-base-form"
                    disabled={isSubmitting || !canEdit}
                    size="sm"
                    className="gap-2"
                >
                    {isSubmitting ? (
                        <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {isEditMode ? 'Saving...' : 'Creating...'}
                        </>
                    ) : (
                        <>
                            {isEditMode ? (
                                <>
                                    <Save className="h-4 w-4" />
                                    Save Changes
                                </>
                            ) : (
                                <>
                                    <Plus className="h-4 w-4" />
                                    Create
                                </>
                            )}
                        </>
                    )}
                </Button>
            </SiteHeader>

            <div className="flex-1 overflow-auto">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-4 py-6 md:px-8 md:py-8"
                >
                    <form id="knowledge-base-form" onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column */}
                <motion.div variants={itemVariants} className="space-y-6">
                    {/* Basic Settings Card */}
                    <Card className="shadow-sm">
                        <CardHeader className="border-b bg-muted/20">
                            <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                    <Settings2 className="h-4 w-4 text-primary" />
                                </div>
                                <div>
                                    <CardTitle className="text-base">Basic Settings</CardTitle>
                                    <CardDescription className="text-xs text-muted-foreground/70">Update the basic knowledge base settings</CardDescription>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-6 space-y-6">
                            {/* ID Field - Only show in edit mode */}
                            {isEditMode && (
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Hash className="h-4 w-4 text-primary" />
                                        ID
                                    </Label>
                                    <div className="flex items-center gap-2">
                                        <div className="flex-1 px-3 py-2 text-sm bg-muted rounded-md font-mono h-9 flex items-center">
                                            {knowledgeBase?.id}
                                        </div>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="icon"
                                            onClick={copyId}
                                            className="h-9 w-9"
                                        >
                                            {copied ? (
                                                <Check className="h-4 w-4 text-green-500" />
                                            ) : (
                                                <Copy className="h-4 w-4" />
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* Name Field */}
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Type className="h-4 w-4 text-primary" />
                                    Name
                                </Label>
                                <Input
                                    value={knowledgeBase?.name || ''}
                                    onChange={(e) => setKnowledgeBase(prev => prev ? ({
                                        ...prev,
                                        name: e.target.value
                                    }) : null)}
                                    placeholder="Enter knowledge base name"
                                    className="h-9"
                                />
                            </div>

                            {/* File Selection */}
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Database className="h-4 w-4 text-primary" />
                                    Files
                                </Label>
                                {isLoadingFiles ? (
                                    <Skeleton className="h-9 w-full" />
                                ) : (
                                    <Combobox
                                        options={files.map((file) => ({
                                            label: file.name,
                                            value: file.id,
                                            description: file.id
                                        }))}
                                        value={selectedFileIds}
                                        onValueChange={handleFileSelect}
                                        placeholder="Select files..."
                                        emptyMessage="No files available"
                                        mode="multiple"
                                        searchPlaceholder="Search files..."
                                    />
                                )}
                            </div>

                            {/* Websites Section */}
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Globe className="h-4 w-4 text-primary" />
                                    Websites
                                </Label>
                                <div className="flex gap-2">
                                    <Input
                                        value={newWebsite}
                                        onChange={(e) => setNewWebsite(e.target.value)}
                                        placeholder="Enter website URL (https://example.com)"
                                        className="flex-1 h-9"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleAddWebsite();
                                            }
                                        }}
                                    />
                                    <Button 
                                        type="button" 
                                        size="icon"
                                        onClick={handleAddWebsite}
                                        disabled={!newWebsite}
                                    >
                                        <PlusCircle className="h-4 w-4" />
                                    </Button>
                                </div>
                                {knowledgeBase?.metadata?.sources?.websites && knowledgeBase.metadata.sources.websites.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {knowledgeBase.metadata.sources.websites.map((website, index: number) => (
                                            <div
                                                key={index}
                                                className="flex items-center gap-2 bg-secondary text-secondary-foreground px-3 py-1.5 rounded-md text-sm"
                                            >
                                                <span>{website}</span>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleRemoveWebsite(website)}
                                                    className="h-4 w-4 p-0"
                                                >
                                                    ×
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Search Settings Card */}
                    <Card className="shadow-sm">
                        <CardHeader className="border-b bg-muted/20">
                            <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                    <Search className="h-4 w-4 text-primary" />
                                </div>
                                <div>
                                    <CardTitle className="text-base">Search Configuration</CardTitle>
                                    <CardDescription className="text-xs text-muted-foreground/70">Configure search and processing settings</CardDescription>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-6 space-y-6">
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Search className="h-4 w-4 text-primary" />
                                    Search Type
                                </Label>
                                <Select
                                    value={knowledgeBase.searchType}
                                    onValueChange={(value: SearchType) => setKnowledgeBase(prev => prev ? ({
                                        ...prev,
                                        searchType: value
                                    }) : null)}
                                >
                                    <SelectTrigger className="h-9">
                                        <SelectValue placeholder="Select search type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {SEARCH_TYPES.map(type => (
                                            <SelectItem key={type.value} value={type.value}>
                                                {type.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <SwitchWithLabel
                                label="Remove Stop Words"
                                icon={Settings2}
                                tooltip="Remove common words that don't add meaning to improve search accuracy"
                                checked={knowledgeBase.chunkingConfig.removeStopWords || false}
                                onCheckedChange={(checked) => setKnowledgeBase(prev => prev ? ({
                                    ...prev,
                                    chunkingConfig: {
                                        ...prev.chunkingConfig,
                                        removeStopWords: checked
                                    }
                                }) : null)}
                            />

                            <div className="space-y-2">
                                <Label htmlFor="scoreThreshold" className="flex items-center gap-2">
                                    <Tag className="h-4 w-4 text-primary" />
                                    Score Threshold
                                </Label>
                                <Input
                                    type="number"
                                    step="0.1"
                                    value={knowledgeBase.scoreThreshold}
                                    onChange={(e) => setKnowledgeBase(prev => prev ? ({
                                        ...prev,
                                        scoreThreshold: parseFloat(e.target.value)
                                    }) : null)}
                                    placeholder="Enter threshold (0 for no threshold)"
                                    className="h-9"
                                />
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Right Column */}
                <motion.div variants={itemVariants} className="space-y-6">
                    {/* Chunking Settings Card */}
                    <Card className="shadow-sm">
                        <CardHeader className="border-b bg-muted/20">
                            <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                    <Blocks className="h-4 w-4 text-primary" />
                                </div>
                                <div>
                                    <CardTitle className="text-base">Chunking Configuration</CardTitle>
                                    <CardDescription className="text-xs text-muted-foreground/70">Configure document splitting settings</CardDescription>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-6 space-y-6">
                            <div className="space-y-2">
                                <Label htmlFor="targetSplits" className="flex items-center gap-2">
                                    <Hash className="h-4 w-4 text-primary" />
                                    Target Splits per Chunk
                                </Label>
                                <Input
                                    id="targetSplits"
                                    type="number"
                                    value={knowledgeBase.chunkingConfig.targetSplitsPerChunk}
                                    onChange={(e) => setKnowledgeBase(prev => {
                                        if (!prev) return prev;
                                        return {
                                            ...prev,
                                            chunkingConfig: {
                                                ...prev.chunkingConfig,
                                                targetSplitsPerChunk: parseInt(e.target.value)
                                            }
                                        };
                                    })}
                                    placeholder="Default: 20"
                                    className="h-9"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="splitDelimiters" className="flex items-center gap-2">
                                    <PlusCircle className="h-4 w-4 text-primary" />
                                    Split Delimiters
                                </Label>
                                <div className="flex gap-2">
                                    <Input
                                        id="splitDelimiters"
                                        value={newDelimiter}
                                        onChange={(e) => setNewDelimiter(e.target.value)}
                                        placeholder="Enter delimiter (e.g., . ! ? \n \t)"
                                        className="flex-1 h-9"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleAddDelimiter();
                                            }
                                        }}
                                    />
                                    <Button 
                                        type="button" 
                                        size="icon"
                                        onClick={handleAddDelimiter}
                                        disabled={!newDelimiter}
                                        className="h-9 w-9"
                                    >
                                        <PlusCircle className="h-4 w-4" />
                                    </Button>
                                </div>
                                {knowledgeBase?.chunkingConfig?.splitDelimiters && knowledgeBase.chunkingConfig.splitDelimiters.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {knowledgeBase.chunkingConfig.splitDelimiters.map((delimiter: string, index: number) => (
                                            <div
                                                key={index}
                                                className="flex items-center gap-2 bg-secondary text-secondary-foreground px-3 py-1.5 rounded-md text-sm"
                                            >
                                                <span className="font-mono">{formatDelimiter(delimiter)}</span>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleRemoveDelimiter(delimiter)}
                                                    className="h-4 w-4 p-0"
                                                >
                                                    ×
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <SwitchWithLabel
                                label="Rebalance Chunks"
                                icon={Blocks}
                                tooltip="Evenly distribute remainder splits across chunks to ensure balanced chunk sizes"
                                checked={knowledgeBase.chunkingConfig.rebalanceChunks ?? true}
                                onCheckedChange={(checked) => setKnowledgeBase(prev => {
                                    if (!prev) return prev;
                                    return {
                                        ...prev,
                                        chunkingConfig: {
                                            ...prev.chunkingConfig,
                                            rebalanceChunks: checked
                                        }
                                    };
                                })}
                            />
                        </CardContent>
                    </Card>
                </motion.div>
            </form>
                </motion.div>
            </div>

            <ConfirmationDialog
                open={showDeleteDialog}
                onOpenChange={setShowDeleteDialog}
                title="Delete Knowledge Base"
                description={`Are you sure you want to delete "${knowledgeBase?.name || 'Unnamed'}"? This action cannot be undone.`}
                onConfirm={handleDelete}
                isLoading={isDeleting}
                confirmText="Delete"
                variant="destructive"
            />
        </div>
    );
};

export default KnowledgeBaseForm; 